import { ChevronDown, ChevronRight } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { isRouteDirty, type DailyRoutesDraft } from "@/lib/dailyRoutes/draft"
import { AddStopForm } from "./AddStopForm"
import { StopRow } from "./StopRow"
import { useTranslation } from "react-i18next"

type RouteSectionProps = {
  route: string
  draft: DailyRoutesDraft
  expanded: boolean
  // a save is in flight somewhere on the page: nothing may be edited
  saving: boolean
  error: string | null
  onToggle: () => void
  onChange: (next: DailyRoutesDraft) => void
  onSave: () => void
  onDiscard: () => void
}

export function RouteSection({
  route,
  draft,
  expanded,
  saving,
  error,
  onToggle,
  onChange,
  onSave,
  onDiscard,
}: RouteSectionProps) {
  const { t } = useTranslation()
  const codes = draft.orders[route] ?? []
  const dirty = isRouteDirty(draft, route)
  const Chevron = expanded ? ChevronDown : ChevronRight
  return (
    <section className="rounded-lg border">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <Chevron className="h-4 w-4 shrink-0" aria-hidden />
          <span className="font-semibold">{route}</span>
          <span className="text-sm text-muted-foreground">
            {t("maps.routeSection.stops", { count: codes.length })}
          </span>
          {dirty && (
            <Badge variant="info">{t("maps.routeSection.unsaved")}</Badge>
          )}
        </button>
        {dirty && (
          <>
            <Button type="button" size="sm" disabled={saving} onClick={onSave}>
              {t("common.save")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={onDiscard}
            >
              {t("maps.routeSection.discard")}
            </Button>
          </>
        )}
      </div>
      {error && (
        <p role="alert" className="px-3 pb-2 text-sm text-destructive">
          {error}
        </p>
      )}
      {expanded && (
        <div className="flex flex-col gap-2 border-t px-3 py-2">
          <ol className="flex flex-col">
            {codes.map((code, index) => (
              <StopRow
                key={`${index}-${code}`}
                route={route}
                draft={draft}
                code={code}
                index={index}
                saving={saving}
                onChange={onChange}
              />
            ))}
          </ol>
          <div>
            <AddStopForm
              route={route}
              draft={draft}
              disabled={saving}
              onChange={onChange}
            />
          </div>
        </div>
      )}
    </section>
  )
}
